// ===================================================================
// Tracking utilities — GA4 + GTM + Google Ads
// ===================================================================
// Tracking IDs are stored in site_content (key="tracking") and fetched
// at runtime by TrackingProvider. This file exposes:
//   - initTracking(config): injects scripts (GTM and/or GA4) into <head>
//   - trackPageview(path): SPA pageview
//   - track*(...) helpers: event-specific helpers
//
// All events are sent in two ways for maximum compatibility:
//   1) window.dataLayer.push(...)  → consumed by GTM (if installed)
//   2) window.gtag('event', ...)   → consumed by gtag.js (GA4 + Ads)
// ===================================================================

let _config = null;
let _initialized = false;

const DEFAULT_CONFIG = {
  ga4_id: "",            // e.g. "G-XXXXXXXXXX"
  gtm_id: "",            // e.g. "GTM-XXXXXXX"
  gads_conversion_id: "",// e.g. "AW-XXXXXXXXX"
  gads_labels: {         // e.g. "abcDEF123" (the part after the slash)
    form: "",
    call: "",
    email: "",
    visit_request: "",
    listing_view: "",
    document_download: "",
  },
};

export function getTrackingConfig() {
  return _config || DEFAULT_CONFIG;
}

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Ensure gtag function exists (no-op until gtag.js loads)
  if (!window.gtag) {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
}

function injectGtm(gtmId) {
  if (typeof document === "undefined" || !gtmId) return;
  if (document.getElementById("__gtm-script")) return;
  ensureDataLayer();
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const s = document.createElement("script");
  s.id = "__gtm-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  document.head.appendChild(s);
  // <noscript> iframe fallback
  const ns = document.createElement("noscript");
  const ifr = document.createElement("iframe");
  ifr.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
  ifr.height = "0"; ifr.width = "0"; ifr.style.display = "none"; ifr.style.visibility = "hidden";
  ns.appendChild(ifr);
  document.body && document.body.insertBefore(ns, document.body.firstChild);
}

function injectGtag(ga4Id, gadsId) {
  if (typeof document === "undefined") return;
  if (!ga4Id && !gadsId) return;
  ensureDataLayer();
  const primary = ga4Id || gadsId;
  if (!document.getElementById("__gtag-script")) {
    const s = document.createElement("script");
    s.id = "__gtag-script";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primary)}`;
    document.head.appendChild(s);
    window.gtag("js", new Date());
  }
  if (ga4Id) {
    window.gtag("config", ga4Id, { send_page_view: false });
  }
  if (gadsId) {
    window.gtag("config", gadsId);
  }
}

export function initTracking(config) {
  if (_initialized) return;
  _config = { ...DEFAULT_CONFIG, ...(config || {}), gads_labels: { ...DEFAULT_CONFIG.gads_labels, ...((config || {}).gads_labels || {}) } };
  ensureDataLayer();

  // GTM takes precedence; if both GTM + GA4 are set, GA4 should be configured inside GTM.
  if (_config.gtm_id) {
    injectGtm(_config.gtm_id);
  }
  // gtag.js needed for direct GA4 (when no GTM) AND for direct Google Ads conversions.
  if ((!_config.gtm_id && _config.ga4_id) || _config.gads_conversion_id) {
    injectGtag(_config.gtm_id ? "" : _config.ga4_id, _config.gads_conversion_id);
  }
  _initialized = true;
}

// ----------------- Internal push helpers -----------------
function push(event, params) {
  ensureDataLayer();
  window.dataLayer.push({ event, ...params });
}

function gaEvent(name, params) {
  ensureDataLayer();
  // Send to gtag (GA4) if loaded directly. If only GTM is used, GTM will pick up
  // dataLayer events and forward to GA4 via tag config.
  if (_config?.ga4_id && !_config?.gtm_id && typeof window.gtag === "function") {
    window.gtag("event", name, params || {});
  }
  // Always push to dataLayer (GTM consumers + custom listeners)
  push(name, params || {});
}

function adsConversion(labelKey, params = {}) {
  const cfg = _config || DEFAULT_CONFIG;
  if (!cfg.gads_conversion_id) return;
  const label = (cfg.gads_labels || {})[labelKey];
  if (!label) return;
  ensureDataLayer();
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: `${cfg.gads_conversion_id}/${label}`,
      ...params,
    });
  }
}

// ----------------- Public API -----------------
export function trackPageview(path) {
  const url = path || (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
  // GA4 page_view
  if (_config?.ga4_id && !_config?.gtm_id && typeof window?.gtag === "function") {
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: url,
      page_title: document.title,
    });
  }
  push("page_view", { page_path: url, page_title: typeof document !== "undefined" ? document.title : "" });
}

export function trackFormSubmit(formName, extra = {}) {
  gaEvent("form_submit", { form_name: formName, ...extra });
  adsConversion("form", { event_callback: undefined, ...extra });
}

export function trackPhoneClick(phone) {
  gaEvent("phone_click", { phone_number: phone });
  adsConversion("call", { phone_number: phone });
}

export function trackEmailClick(email) {
  gaEvent("email_click", { email });
  adsConversion("email", { email });
}

export function trackListingView(listing) {
  // listing: { id, title, price, city, type }
  gaEvent("view_item", {
    item_id: listing?.id,
    item_name: listing?.title,
    item_category: listing?.type,
    item_location_id: listing?.city,
    value: listing?.price,
    currency: "EUR",
  });
  adsConversion("listing_view", { value: listing?.price, currency: "EUR" });
}

export function trackDocumentDownload(file) {
  // file: { name, url }
  gaEvent("file_download", { file_name: file?.name, file_url: file?.url });
  adsConversion("document_download", {});
}

export function trackVisitRequest(extra = {}) {
  gaEvent("visit_request", extra);
  adsConversion("visit_request", extra);
}

// Generic helper for custom events
export function trackEvent(name, params) {
  gaEvent(name, params);
}
